import { withBreakpointObserver } from '@studiometa/js-toolkit';
import { nextFrame, inertiaFinalValue, clamp } from '@studiometa/js-toolkit/utils';
import {
  Slider as SliderCore,
  SliderItem,
  SliderDrag,
  SliderProgress,
  SliderBtn,
  SliderDots,
  SliderCount,
} from '@studiometa/ui';

/**
 * Slider component
 */
export default class Slider extends withBreakpointObserver(SliderCore) {
  /**
   * Component Configuration
   */
  static config = {
    ...SliderCore.config,
    name: 'Slider',
    components: {
      ...SliderCore.config.components,
      SliderItem,
      SliderBtn,
      SliderDrag,
      SliderDots,
      SliderCount,
      SliderProgress,
    },
  };

  /**
   * Get the last index.
   * @description Use this.states.length instead of this.$children.SliderItem.length to have correct calculation when using the contain option.
   * @returns {number}
   */
  get indexMax() {
    return this.states.length - 1;
  }

  /**
   * Get the states for each SliderItem.
   * @returns {Array}
   */
  getStates() {
    const { wrapper } = this.$refs;
    const originRect = wrapper.getBoundingClientRect();

    this.origins = {
      left: originRect.left,
      center: originRect.x + originRect.width / 2,
      right: originRect.x + originRect.width,
    };

    const states = this.$children.SliderItem.map((item) => ({
      x: {
        left: (item.rect.x - this.origins.left) * -1,
        center: (item.rect.x + item.rect.width / 2 - this.origins.center) * -1,
        right: (item.rect.x + item.rect.width - this.origins.right) * -1,
      },
    }));

    if (this.$options.contain) {
      const { mode } = this.$options;
      // Find state where last child has passed the wrapper bound completely
      if (mode === 'left') {
        const lastChild = this.$children.SliderItem.at(-1);

        const maxState = states.find((state) => {
          const lastChildPosition =
            lastChild.rect.x - this.origins.left + lastChild.rect.width + state.x.left;
          const diffWithWrapperBound = originRect.width - lastChildPosition;
          if (diffWithWrapperBound > 0) {
            state.x.left = Math.min(state.x.left + diffWithWrapperBound, 0);

            return true;
          }
          return false;
        });

        if (maxState) {
          states.map((state) => {
            state.x.left = Math.max(state.x.left, maxState.x.left);
            return state;
          });

          // @note filter the states to remove duplicate state, in order to remove the number of slides to respect the contain option
          let iterable = 0;
          const s = states.filter((state) => {
            if (state.x.left === maxState.x.left) {
              if (iterable === 0) {
                iterable += 1;
                return true;
              }
              return false;
            }
            return true;
          });

          return s;
        }
      }

      if (mode === 'right') {
        const maxStateIndex = states.findIndex((state) => state.x.right <= 0);
        const maxState = maxStateIndex < 0 ? states.at(-1) : states[maxStateIndex - 1];

        return states.map((state) => {
          state.x.right = maxStateIndex < 0 ? maxState.x.right : Math.min(state.x.right, 0);
          return state;
        });
      }
    }

    return states;
  }

  // eslint-disable-next-line jsdoc/require-param
  /**
   * Go to the given index.
   */
  goTo(index, { withInstantMove = true } = {}) {
    if (index < 0 || index > this.indexMax) {
      throw new Error('Index out of bound.');
    }

    const currentState = this.getStateValueByMode(this.currentState.x);

    // @note Test if the state exist since they might have been filtered
    if (this.states[index]) {
      const state = this.getStateValueByMode(this.states[index].x);
      const itemsToMove = this.getVisibleItems(state);
      const invisibleItemsToMoveInstantly = this.getInvisibleItems(state);
      itemsToMove.forEach((item) => {
        // Better perfs when going fast through the slides
        if (currentState !== state && withInstantMove) {
          item.moveInstantly(currentState);
        }
        nextFrame(() => item.move(state));
      });
      invisibleItemsToMoveInstantly.forEach((item) => {
        item.moveInstantly(state);
      });

      this.currentIndex = index;
      this.$emit('goto', index);
    }
  }

  /**
   * getVisibleItems
   * @returns {[]}
   */
  getVisibleItems() {
    return this.$children.SliderItem;
  }

  /**
   * Consider all items as visible.
   * @returns {[]}
   */
  getInvisibleItems() {
    return [];
  }

  /**
   * onSliderDragDrop
   * @param {object} obj
   * @param {object} obj.args
   */
  onSliderDragDrop({ args }) {
    const props = args[0];

    let finalX = clamp(
      inertiaFinalValue(this.__distanceX, props.delta.x * this.$options.dropSensitivity),
      this.getStateValueByMode(this.firstState?.x),
      this.getStateValueByMode(this.lastState.x),
    );
    const absoluteDifferencesBetweenDistanceAndState = this.states.map((state) =>
      Math.abs(finalX - this.getStateValueByMode(state.x)),
    );
    const minimumDifference = Math.min(...absoluteDifferencesBetweenDistanceAndState);
    const closestIndex = absoluteDifferencesBetweenDistanceAndState.indexOf(minimumDifference);
    if (this.$options.fitBounds) {
      this.goTo(closestIndex);
    } else {
      if (this.$options.contain) {
        finalX = Math.min(this.containMinState, finalX);
        finalX = Math.max(this.containMaxState, finalX);
      }
      for (const item of this.$children.SliderItem) {
        item.move(finalX);
      }
      this.currentIndex = closestIndex;
    }
  }
}
